import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfiniteData,
	InfinitivePage,
	SuccessCursorResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	AppendTicketMessagePayload,
	CreateTicketPayload,
	Ticket,
	TicketMessage,
} from "interface/support"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { sortTicketMessages, sortTickets } from "modules/support/utils"
import { useMemo } from "react"
import { getBase64, isBase64DataUrl } from "utils/file"

export const queryKeys = {
	getTickets: "get-tickets",
	getTicket: "get-ticket",
	createTicket: "create-ticket",
	closeTicket: "close-ticket",
	appendTicketMessage: "append-ticket-message",
	getAttachment: "get-attachment",
	getUpdates: "get-patient-ticket-updates",
}

export const useTickets = (patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.getTickets, patientId], [patientId])

	useInvalidateOnRealtimeEvent(["ticket_updated", "ticket_deleted"], {
		queryKey,
		queryPredicate(query, data) {
			const infiniteTickets = query.state.data as
				InfiniteData<Ticket> | undefined
			const hasTicket = infiniteTickets?.pages.some((page) =>
				page.data.some(({ _id }) => _id === data.ticketId),
			)
			return hasTicket ?? false
		},
	})

	return useInfiniteQuery<InfinitivePage<Ticket>, Error, { pages: Ticket[] }>({
		queryKey,
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<Ticket>>(
				"/api/v1/support/tickets",
				{
					signal,
					params: {
						patientId,
						cursor: pageParam,
					},
				},
			)

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		initialPageParam: undefined,
		placeholderData: keepPreviousData,
		select: ({ pages }) => {
			return {
				pages: pages.flatMap(({ data }) => data).toSorted(sortTickets),
			}
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	})
}

export const useTicket = (ticketId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.getTicket, ticketId], [ticketId])

	useInvalidateOnRealtimeEvent("ticket_updated", {
		queryKey,
		predicate: (data) => ticketId === data.ticketId,
	})
	useInvalidateOnRealtimeEvent("ticket_deleted", {
		queryKey,
		predicate: (data) => ticketId === data.ticketId,
		action: "remove",
	})

	return useQuery<Ticket, ErrorResponse>({
		enabled: !!ticketId,
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Ticket>>(
				`/api/v1/support/tickets/${ticketId}`,
				{
					signal,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
		select: ({ messages = [], ...ticket }) => {
			const firstMessage: TicketMessage = {
				id: "initial-message",
				authorType: "CREATOR",
				createdAt: ticket.createdAt,
				content: ticket.initialMessage,
				attachments: ticket.attachments,
				authorDisplayName: ticket.creatorDisplayName,
			}
			return {
				...ticket,
				messages: [firstMessage, ...messages].toSorted(sortTicketMessages),
			}
		},
	})
}

export const useCreateTicket = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Ticket, ErrorResponse, CreateTicketPayload>({
		mutationKey: [queryKeys.createTicket],
		mutationFn: async (payload: CreateTicketPayload) => {
			const formData = asFormData(payload)
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<Ticket>>(
				"/api/v1/support/tickets",
				formData,
			)
			return data
		},
		onSuccess: (ticket, { patientContextId }) => {
			queryClient.setQueryData<Ticket>(
				[queryKeys.getTicket, ticket._id],
				ticket,
			)

			queryClient.setQueryData<{ pages: InfinitivePage<Ticket>[] }>(
				[queryKeys.getTickets, patientContextId],
				(prev) => {
					if (!prev) {
						return prev
					}
					const [head, ...rest] = prev.pages ?? []
					return {
						...prev,
						pages: [{ ...head, data: [ticket, ...(head.data ?? [])] }, ...rest],
					}
				},
			)
		},
	})
}

export const useCloseTicket = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Ticket, ErrorResponse, Ticket["_id"]>({
		mutationKey: [queryKeys.closeTicket],
		mutationFn: async (ticketId: Ticket["_id"]) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<Ticket>>(
				`/api/v1/support/tickets/${ticketId}/close`,
			)
			return data
		},
		onSuccess: (ticket) => {
			queryClient.setQueryData<Ticket>(
				[queryKeys.getTicket, ticket._id],
				(prevTicket) => ({ ...ticket, messages: prevTicket?.messages }),
			)

			queryClient.setQueryData<{ pages: InfinitivePage<Ticket>[] }>(
				[queryKeys.getTickets, ticket.patientId],
				(prev) => {
					if (!prev) {
						return prev
					}
					return {
						...prev,
						pages: prev.pages.map((page) => ({
							...page,
							data: page.data.map((existingTicket) =>
								existingTicket._id === ticket._id ? ticket : existingTicket,
							),
						})),
					}
				},
			)

			// Refresh the "Needs Attention" badge immediately instead of waiting
			// for the Linear webhook round-trip to invalidate it.
			queryClient.invalidateQueries({ queryKey: [queryKeys.getUpdates] })
		},
	})
}

export const useAppendTicketMessage = (ticketId?: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<TicketMessage, ErrorResponse, AppendTicketMessagePayload>({
		mutationKey: [queryKeys.appendTicketMessage, ticketId],
		mutationFn: async (payload: AppendTicketMessagePayload) => {
			const formData = asFormData(payload)
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<TicketMessage>>(
				`/api/v1/support/tickets/${ticketId}/messages`,
				formData,
			)
			return data
		},
		onSuccess: async (ticketMessage) => {
			const cachedTicket = queryClient.getQueryData<Ticket>([
				queryKeys.getTicket,
				ticketId,
			])

			if (
				cachedTicket &&
				["Closed", "Cancelled"].includes(cachedTicket.status)
			) {
				queryClient.invalidateQueries({
					queryKey: [queryKeys.getTicket, ticketId],
				})
				queryClient.invalidateQueries({
					queryKey: [queryKeys.getTickets, cachedTicket.patientId],
				})

				return
			}

			queryClient.setQueryData<Ticket>(
				[queryKeys.getTicket, ticketId],
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						messages: prev.messages
							? [...prev.messages, ticketMessage]
							: [ticketMessage],
					}
				},
			)

			queryClient.setQueryData<{ pages: InfinitivePage<Ticket>[] }>(
				[queryKeys.getTickets, cachedTicket?.patientId],
				(prev) => {
					if (!prev) {
						return prev
					}
					return {
						...prev,
						pages: prev.pages.map((page) => ({
							...page,
							data: page.data.map((ticket) =>
								ticket._id === ticketId
									? {
											...ticket,
											messages: [...(ticket.messages ?? []), ticketMessage],
										}
									: ticket,
							),
						})),
					}
				},
			)
		},
	})
}

export const useAttachment = (attachmentUrl: string) => {
	const { httpClient } = useHttpClient()

	return useQuery<string, ErrorResponse>({
		queryKey: [queryKeys.getAttachment, attachmentUrl],
		queryFn: async ({ signal }) => {
			if (isBase64DataUrl(attachmentUrl)) {
				return attachmentUrl
			}

			const response = await httpClient.get<Blob>(attachmentUrl, {
				signal,
				responseType: "blob",
				validateStatus: () => true,
			})

			const contentType = response.headers["content-type"]

			if (contentType?.includes("application/json")) {
				const text = await response.data.text() // Read blob as text
				const json = JSON.parse(text)
				throw new Error(json.message || "Download failed")
			}

			return getBase64(response.data)
		},
		placeholderData: keepPreviousData,
	})
}

export const useTicketUpdates = (forcePatient = false, patientId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.getUpdates, patientId], [patientId])

	useInvalidateOnRealtimeEvent(["ticket_updated", "ticket_deleted"], {
		queryKey,
	})

	return useQuery({
		enabled: forcePatient ? !!patientId : true,
		queryKey,
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<{ count: number }>>(
				"/api/v1/support/tickets/updates/count",
				{
					signal,
					params: {
						patientId,
					},
				},
			)
		},
		placeholderData: keepPreviousData,
		select({ data }) {
			return data.data.count
		},
	})
}

function asFormData(payload: CreateTicketPayload | AppendTicketMessagePayload) {
	const formData = new FormData()

	for (const key of Object.getOwnPropertyNames(payload)) {
		const value = payload[key as keyof typeof payload]
		if (key === "files") {
			for (const file of (value as File[]) ?? []) {
				formData.append("files", file)
			}
		} else if (value !== undefined) {
			formData.append(key, String(value))
		}
	}

	return formData
}
